import React, { useEffect, useState } from 'react';
import type {
  CreateSeasonRequest,
  UpdateSeasonRequest,
  Season,
  Division,
  Category,
} from '@sulis-liiga/common';
import { SeasonStatus } from '@sulis-liiga/common';
import leagueService from '../../services/leagueService';

interface SeasonFormProps {
  initialData?: Partial<CreateSeasonRequest | UpdateSeasonRequest>;
  onSubmit: (data: CreateSeasonRequest | UpdateSeasonRequest) => void;
  onCancel: () => void;
  loading: boolean;
  isEdit?: boolean;
  /** Existing seasons to offer as "borrow divisions from" source (CREATE mode only). */
  existingSeasons?: Season[];
  /**
   * League ID — when provided together with a selected borrow season, the form
   * fetches that season's divisions and shows a per-tier Max Teams editor so the
   * admin can resize tiers during the transition.
   */
  leagueId?: string;
  /**
   * Pre-select a "borrow divisions from" source (CREATE mode only). Used by the
   * season-transition flow to default to the season being finalized so the new
   * season copies its ladder structure without the admin having to pick it.
   */
  defaultBorrowSeasonId?: string;
}

const SeasonForm: React.FC<SeasonFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  loading,
  isEdit = false,
  existingSeasons = [],
  leagueId,
  defaultBorrowSeasonId,
}) => {
  const [borrowSeasonId, setBorrowSeasonId] = useState(!isEdit ? defaultBorrowSeasonId ?? '' : '');
  const [carryOverPlayers, setCarryOverPlayers] = useState(true);
  const [formData, setFormData] = useState({
    name: initialData?.name || '',
    startDate: initialData?.startDate
      ? new Date(initialData.startDate).toISOString().split('T')[0]
      : '',
    endDate: initialData?.endDate ? new Date(initialData.endDate).toISOString().split('T')[0] : '',
    pointsExpiration: initialData?.pointsExpiration
      ? new Date(initialData.pointsExpiration).toISOString().split('T')[0]
      : '',
    status: (initialData as UpdateSeasonRequest)?.status || SeasonStatus.UPCOMING,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // ─── Per-tier override state ─────────────────────────────────────────────────
  // sourceDivisions: divisions from the borrowed season (grouped by ladder for display)
  const [sourceDivisions, setSourceDivisions] = useState<Division[]>([]);
  // tierOverrides: key = `${category}:${baseName}:${level}` → maxTeams
  const [tierOverrides, setTierOverrides] = useState<Record<string, number>>({});
  const [divisionsLoading, setDivisionsLoading] = useState(false);

  // Fetch source season divisions whenever the borrow selection + leagueId are both present
  useEffect(() => {
    if (!borrowSeasonId || !leagueId) {
      setSourceDivisions([]);
      setTierOverrides({});
      return;
    }

    setDivisionsLoading(true);
    leagueService
      .getDivisionsForSeason(leagueId, borrowSeasonId)
      .then(divs => {
        setSourceDivisions(divs);
        // Pre-fill override values from the source division's current maxTeams
        const initialOverrides: Record<string, number> = {};
        divs.forEach(d => {
          if (d.baseName) {
            initialOverrides[`${d.category}:${d.baseName}:${d.level}`] = d.maxTeams;
          }
        });
        setTierOverrides(initialOverrides);
      })
      .catch(() => {
        setSourceDivisions([]);
      })
      .finally(() => setDivisionsLoading(false));
  }, [borrowSeasonId, leagueId]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  /** Group source divisions by (category, baseName/divisionGroupId), sorted by level. */
  const ladderGroups = React.useMemo(() => {
    const map = new Map<string, Division[]>();
    for (const d of sourceDivisions) {
      if (!d.baseName) continue; // skip old-format divisions without baseName
      const key = `${d.category}:${d.baseName}`;
      const bucket = map.get(key) ?? [];
      if (!map.has(key)) map.set(key, bucket);
      bucket.push(d);
    }
    Array.from(map.values()).forEach(ladder => ladder.sort((a, b) => a.level - b.level));
    return Array.from(map.values());
  }, [sourceDivisions]);

  const hasTierEditor = leagueId && borrowSeasonId && ladderGroups.length > 0;

  const handleTierOverride = (
    category: Category,
    baseName: string,
    level: number,
    value: string,
  ) => {
    const maxTeams = Math.max(2, parseInt(value) || 2);
    setTierOverrides(prev => ({
      ...prev,
      [`${category}:${baseName}:${level}`]: maxTeams,
    }));
  };

  // ─── Validation ──────────────────────────────────────────────────────────────

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Season name is required';
    }

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (!formData.pointsExpiration) {
      newErrors.pointsExpiration = 'Points expiration date is required';
    }

    if (formData.startDate && formData.endDate && formData.startDate >= formData.endDate) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─── Submit ──────────────────────────────────────────────────────────────────

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData: CreateSeasonRequest & Partial<UpdateSeasonRequest> = {
      name: formData.name,
      startDate: new Date(formData.startDate),
      endDate: new Date(formData.endDate),
      pointsExpiration: new Date(formData.pointsExpiration),
    };

    if (isEdit) {
      submitData.status = formData.status;
    }

    if (!isEdit) {
      (submitData as CreateSeasonRequest).carryOverPlayers = carryOverPlayers;
      if (borrowSeasonId) {
        (submitData as CreateSeasonRequest).borrowDivisionsFromSeasonId = borrowSeasonId;
      }

      // Build divisionTierOverrides from any changed maxTeams values
      if (borrowSeasonId && Object.keys(tierOverrides).length > 0) {
        const overrides = Object.entries(tierOverrides).map(([key, maxTeams]) => {
          const [category, baseName, levelStr] = key.split(':');
          return {
            category: category as Category,
            baseName,
            level: parseInt(levelStr),
            maxTeams,
          };
        });
        if (overrides.length > 0) {
          (submitData as CreateSeasonRequest).divisionTierOverrides = overrides;
        }
      }
    }

    onSubmit(submitData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700">
          Season Name
        </label>
        <input
          type="text"
          name="name"
          id="name"
          required
          value={formData.name}
          onChange={handleChange}
          className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
            errors.name ? 'border-danger-400' : 'border-gray-300'
          }`}
        />
        {errors.name && <p className="mt-1 text-sm text-danger-600">{errors.name}</p>}
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
            Start Date
          </label>
          <input
            type="date"
            name="startDate"
            id="startDate"
            required
            value={formData.startDate}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.startDate ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.startDate && <p className="mt-1 text-sm text-danger-600">{errors.startDate}</p>}
        </div>

        <div>
          <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
            End Date
          </label>
          <input
            type="date"
            name="endDate"
            id="endDate"
            required
            value={formData.endDate}
            onChange={handleChange}
            className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
              errors.endDate ? 'border-danger-400' : 'border-gray-300'
            }`}
          />
          {errors.endDate && <p className="mt-1 text-sm text-danger-600">{errors.endDate}</p>}
        </div>
      </div>

      <div>
        <label htmlFor="pointsExpiration" className="block text-sm font-medium text-gray-700">
          Points Expiration Date
        </label>
        <input
          type="date"
          name="pointsExpiration"
          id="pointsExpiration"
          required
          value={formData.pointsExpiration}
          onChange={handleChange}
          className={`mt-1 appearance-none block w-full px-3 py-2 border rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm ${
            errors.pointsExpiration ? 'border-danger-400' : 'border-gray-300'
          }`}
        />
        {errors.pointsExpiration && (
          <p className="mt-1 text-sm text-danger-600">{errors.pointsExpiration}</p>
        )}
      </div>

      {isEdit && (
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            name="status"
            id="status"
            value={formData.status}
            onChange={handleChange}
            className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value={SeasonStatus.UPCOMING}>Upcoming</option>
            <option value={SeasonStatus.ACTIVE}>Active</option>
            <option value={SeasonStatus.COMPLETED}>Completed</option>
          </select>
        </div>
      )}

      {!isEdit && (
        <>
          {existingSeasons.length > 0 && (
            <div>
              <label htmlFor="borrowSeasonId" className="block text-sm font-medium text-gray-700">
                Borrow divisions from&hellip;{' '}
                <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <select
                id="borrowSeasonId"
                value={borrowSeasonId}
                onChange={e => setBorrowSeasonId(e.target.value)}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
              >
                <option value="">— do not borrow —</option>
                {existingSeasons.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Copies the division structure (no teams) into the new season.
              </p>
            </div>
          )}

          {/* Per-tier Max Teams editor — shown when a borrow season is chosen and
              leagueId is available to fetch its divisions */}
          {borrowSeasonId && leagueId && (
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-gray-700">
                Tier sizes{' '}
                <span className="font-normal text-gray-400">
                  — adjust max teams per tier for the new season
                </span>
              </h3>

              {divisionsLoading && (
                <p className="text-sm text-gray-500">Loading divisions&hellip;</p>
              )}

              {!divisionsLoading && ladderGroups.length === 0 && sourceDivisions.length > 0 && (
                <p className="text-sm text-gray-500">
                  Source divisions do not carry tier metadata; sizes cannot be adjusted here.
                </p>
              )}

              {!divisionsLoading && hasTierEditor && (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Tier
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Max Teams
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {ladderGroups.map(ladder => (
                        <React.Fragment key={`${ladder[0].category}:${ladder[0].baseName}`}>
                          {/* Ladder sub-heading row */}
                          <tr className="bg-gray-50">
                            <td
                              colSpan={2}
                              className="px-4 py-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide"
                            >
                              {ladder[0].category} · {ladder[0].baseName}
                            </td>
                          </tr>
                          {ladder.map(division => {
                            const key = `${division.category}:${division.baseName}:${division.level}`;
                            return (
                              <tr key={division.id}>
                                <td className="px-4 py-2 text-gray-700 pl-8">{division.name}</td>
                                <td className="px-4 py-2">
                                  <input
                                    type="number"
                                    aria-label={`Max teams for ${division.name}`}
                                    min="2"
                                    value={tierOverrides[key] ?? division.maxTeams}
                                    onChange={e =>
                                      handleTierOverride(
                                        division.category,
                                        division.baseName ?? '',
                                        division.level,
                                        e.target.value,
                                      )
                                    }
                                    className="w-24 px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                                  />
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          <div className="flex items-start gap-3">
            <div className="flex items-center h-5">
              <input
                id="carryOverPlayers"
                name="carryOverPlayers"
                type="checkbox"
                checked={carryOverPlayers}
                onChange={e => setCarryOverPlayers(e.target.checked)}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
            </div>
            <div className="ml-3 text-sm">
              <label htmlFor="carryOverPlayers" className="font-medium text-gray-700">
                Carry over players from the previous season
              </label>
              <p className="text-gray-500">
                Players from the current season are auto-subscribed to this one (they can
                unsubscribe).
              </p>
            </div>
          </div>
        </>
      )}

      <div className="flex justify-end space-x-3">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Saving...' : isEdit ? 'Update Season' : 'Create Season'}
        </button>
      </div>
    </form>
  );
};

export default SeasonForm;
